import { z } from 'zod';

/** Every closed vocabulary shared by the server, the app and the content lives here, once. */

/** Languages the app speaks: Traditional (HK written), Simplified and English. */
export const LOCALES = ['zh-HK', 'zh-CN', 'en'] as const;
export const Locale = z.enum(LOCALES);
export type Locale = z.infer<typeof Locale>;
export const DEFAULT_LOCALE: Locale = 'zh-HK';
/**
 * Languages text is written in. Simplified Chinese is never authored: it is converted from the
 * Traditional source (see toSimplified), so the two can't drift apart.
 */
export const AUTHORED_LOCALES = ['zh-HK', 'en'] as const;
export type AuthoredLocale = (typeof AUTHORED_LOCALES)[number];

export const STATS = ['charm', 'confidence', 'conversation', 'empathy', 'observation'] as const;
export const Stat = z.enum(STATS);
export type Stat = z.infer<typeof Stat>;

export const SKILLS = [
  'icebreaker',
  'keep_flowing',
  'genuine_compliment',
  'good_listener',
  'playful_humour',
  'natural_invitation',
] as const;
export const SkillId = z.enum(SKILLS);
export type SkillId = z.infer<typeof SkillId>;

export const LIFE_SKILLS = ['photography', 'music', 'cooking'] as const;
/** Highest level for social and life skills. */
export const SKILL_MAX_LEVEL = 10;

export const PET_SPECIES = ['cat', 'dog', 'rabbit'] as const;
export const PetSpecies = z.enum(PET_SPECIES);
export type PetSpecies = z.infer<typeof PetSpecies>;

export const HOME_SLOTS = ['wall', 'floor', 'desk', 'window', 'bed'] as const;
export const HomeSlot = z.enum(HOME_SLOTS);
export type HomeSlot = z.infer<typeof HomeSlot>;
export const LifeSkillId = z.enum(LIFE_SKILLS);
export type LifeSkillId = z.infer<typeof LifeSkillId>;

export const TRAITS = ['confident', 'observant', 'thoughtful'] as const;
export const Trait = z.enum(TRAITS);
export type Trait = z.infer<typeof Trait>;

export const LIFE_GOALS = ['romance', 'friends', 'dream', 'fresh_start'] as const;
export const LifeGoal = z.enum(LIFE_GOALS);
export type LifeGoal = z.infer<typeof LifeGoal>;

export const GENDERS = ['male', 'female'] as const;
export const Gender = z.enum(GENDERS);
export type Gender = z.infer<typeof Gender>;

export const SLOTS = ['morning', 'afternoon', 'evening'] as const;
export const Slot = z.enum(SLOTS);
export type Slot = z.infer<typeof Slot>;

export const WEATHERS = ['clear', 'cloudy', 'rain'] as const;
export const Weather = z.enum(WEATHERS);
export type Weather = z.infer<typeof Weather>;

/** `seaside` is the first travel region (plan §15 P5 旅行 / P6 新地區); unlocked by play, not by default. */
export const LOCATIONS = ['apartment', 'university', 'cafe', 'park', 'town_square', 'seaside'] as const;
export const LocationId = z.enum(LOCATIONS);
export type LocationId = z.infer<typeof LocationId>;

/**
 * The fifty residents of Inkwell. The first six are the original cast (openings, authored
 * arcs); the rest are met through the personal serial. Append only: order is stable across saves.
 */
export const NPCS = [
  'mia', 'chloe', 'emma', 'ethan', 'daniel', 'leo',
  'ivy', 'hazel', 'june', 'rosa', 'nadia', 'kiki', 'grace', 'olivia', 'ruby', 'tessa', 'vera',
  'clara', 'lily', 'zoe', 'jade', 'naomi', 'coco', 'elaine', 'penny', 'freya', 'amber', 'sophie',
  'marcus', 'kai', 'oscar', 'felix', 'noah', 'theo', 'victor', 'ryan', 'sean', 'owen', 'jason',
  'hugo', 'adrian', 'ben', 'max', 'sam', 'elliot', 'nathan', 'ivan', 'colin', 'patrick', 'lucas',
] as const;
export const NpcId = z.enum(NPCS);
export type NpcId = z.infer<typeof NpcId>;

/**
 * The v1 cast (2026-10-04): sixteen residents tied together by the Lantern Night spine. The other
 * thirty-four are shelved, not deleted: they stay in NPCS so old saves still parse, but nothing new
 * meets, lists, writes or counts them. Every consumer that shows or picks neighbours goes through
 * ACTIVE_NPCS / isActiveNpc.
 */
export const ACTIVE_NPCS = [
  'mia', 'chloe', 'emma', 'ethan', 'daniel', 'leo',
  'amber', 'june', 'tessa', 'max', 'theo', 'sam',
  'hazel', 'rosa', 'nathan', 'ivan',
] as const satisfies readonly NpcId[];
const ACTIVE_SET: ReadonlySet<string> = new Set(ACTIVE_NPCS);
/** Whether this neighbour is in the current cast (false for shelved ones and for non-NPC ids). */
export const isActiveNpc = (id: string): id is NpcId => ACTIVE_SET.has(id);

/** Each character's card colour: one of the theme's soft tints, chosen in their blueprint. */
export const NPC_TINTS = ['accent', 'pop', 'reward', 'bond', 'growth'] as const;
export const NpcTint = z.enum(NPC_TINTS);
export type NpcTint = z.infer<typeof NpcTint>;

export const EXPRESSIONS = ['neutral', 'happy', 'sad', 'surprised', 'shy'] as const;
export const Expression = z.enum(EXPRESSIONS);
export type Expression = z.infer<typeof Expression>;

/** The action taxonomy (plan §11). Free text is always mapped onto one of these. */
export const ACTION_TYPES = [
  'greet',
  'ask',
  'accompany',
  'listen',
  'change_topic',
  'compliment',
  'joke',
  'invite',
  'help',
  'observe',
  'apologize',
  'give',
  'photograph',
  'perform',
  'suggest',
  'respect_boundary',
  'decline',
  'leave',
  'attempt',
] as const;
export const ActionType = z.enum(ACTION_TYPES);
export type ActionType = z.infer<typeof ActionType>;

export const CHECK_TIERS = ['strong', 'normal', 'awkward', 'blocked'] as const;
export const CheckTier = z.enum(CHECK_TIERS);
export type CheckTier = z.infer<typeof CheckTier>;

export const RELATIONSHIP_DIMENSIONS = ['familiarity', 'trust', 'affection', 'romanticInterest', 'tension'] as const;
export const RelationshipDimension = z.enum(RELATIONSHIP_DIMENSIONS);
export type RelationshipDimension = z.infer<typeof RelationshipDimension>;

export const RELATIONSHIP_TYPES = [
  'acquaintance',
  'friend',
  'close_friend',
  'roommate',
  'neighbour',
  'coworker',
  'classmate',
  'bandmate',
  'mentor',
  'rival',
  'activity_partner',
  'dating',
  'partner',
  'former_partner',
  'family',
] as const;
export const RelationshipType = z.enum(RELATIONSHIP_TYPES);
export type RelationshipType = z.infer<typeof RelationshipType>;

/** The player-visible stage ladder derived from dimensions (plan §9). */
export const RELATIONSHIP_STAGES = ['stranger', 'acquainted', 'familiar', 'trusted', 'close', 'dating', 'partner'] as const;
export const RelationshipStage = z.enum(RELATIONSHIP_STAGES);
export type RelationshipStage = z.infer<typeof RelationshipStage>;

export const EPISTEMIC_STATUSES = ['confirmed', 'reported', 'rumour', 'guess'] as const;
export const EpistemicStatus = z.enum(EPISTEMIC_STATUSES);
export type EpistemicStatus = z.infer<typeof EpistemicStatus>;

export const EVENT_KINDS = ['encounter', 'story_chapter', 'shared_chapter'] as const;
export const EventKind = z.enum(EVENT_KINDS);
export type EventKind = z.infer<typeof EventKind>;

export const EPISODE_STATES = ['awaiting_action', 'completed', 'paused', 'abandoned'] as const;
export const EpisodeState = z.enum(EPISODE_STATES);
export type EpisodeState = z.infer<typeof EpisodeState>;

export const JOB_STATES = ['queued', 'running', 'retry_scheduled', 'succeeded', 'failed_permanent', 'cancelled'] as const;
export const JobState = z.enum(JOB_STATES);
export type JobState = z.infer<typeof JobState>;

/** Player-facing manga progress; never a fake percentage (plan §12). */
export const MANGA_PHASES = ['queued', 'storyboard', 'drawing', 'lettering', 'ready', 'failed'] as const;
export const MangaPhase = z.enum(MANGA_PHASES);
export type MangaPhase = z.infer<typeof MangaPhase>;

/** basic = composed from approved art (free, cheap); illustrated = new panel art (credits). */
export const RENDER_TIERS = ['basic', 'illustrated'] as const;
export const RenderTier = z.enum(RENDER_TIERS);
export type RenderTier = z.infer<typeof RenderTier>;

export const SCOPE_KINDS = ['private', 'room', 'community'] as const;
export const ScopeKind = z.enum(SCOPE_KINDS);
export type ScopeKind = z.infer<typeof ScopeKind>;

export const CONVERSATION_KINDS = ['npc_dm', 'player_dm', 'room', 'club'] as const;
export const ConversationKind = z.enum(CONVERSATION_KINDS);
export type ConversationKind = z.infer<typeof ConversationKind>;

export const ROOM_STATES = ['lobby', 'active', 'completed', 'cancelled'] as const;
export const RoomState = z.enum(ROOM_STATES);
export type RoomState = z.infer<typeof RoomState>;

export const REPORT_TARGETS = ['message', 'npc_output', 'episode', 'panel', 'player', 'chapter', 'room'] as const;
export const ReportTarget = z.enum(REPORT_TARGETS);
export type ReportTarget = z.infer<typeof ReportTarget>;

export const REPORT_REASONS = [
  'harassment',
  'sexual_content',
  'minor_safety',
  'hate',
  'spam',
  'wrong_character',
  'story_mismatch',
  'art_quality',
  'other',
] as const;
export const ReportReason = z.enum(REPORT_REASONS);
export type ReportReason = z.infer<typeof ReportReason>;

export const CONSENT_PURPOSES = ['terms', 'ai_processing', 'room_ai_comic', 'public_comic_share', 'marketing', 'notifications'] as const;
export const ConsentPurpose = z.enum(CONSENT_PURPOSES);
export type ConsentPurpose = z.infer<typeof ConsentPurpose>;

export const ADMIN_ROLES = ['viewer', 'support', 'moderator', 'content', 'owner'] as const;
export const AdminRole = z.enum(ADMIN_ROLES);
export type AdminRole = z.infer<typeof AdminRole>;
